import { createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'

/** Terminal provisioning states reported by the account-create status poll. */
export type AccountProvisioningStatus = 'ready' | 'partial_error'

/**
 * Side-effects a form runs after it finishes — distinct from the form's own
 * Save/Cancel controls, which stay in place. Standalone GIM navigates; the
 * embed posts a postMessage to the Workflow parent instead.
 */
export interface OutcomeHandlers {
  /** Account creation reached a terminal provisioning state. */
  onAccountReady: (accountId: number, status: AccountProvisioningStatus) => void
  /** User creation completed successfully. */
  onUserReady: (userId: number) => void
  /** The user dismissed the form via Cancel. */
  onCancel: () => void
}

export const OutcomeContext = createContext<OutcomeHandlers | null>(null)

/**
 * Returns the active outcome handlers. When no OutcomeProvider is mounted (the
 * standalone app), this falls back to navigation — byte-for-byte the behaviour
 * the forms had before embed support, so standalone GIM is unchanged. The embed
 * shell mounts an OutcomeProvider whose handlers post to the parent frame.
 */
export function useOutcome(): OutcomeHandlers {
  const ctx = useContext(OutcomeContext)
  const navigate = useNavigate()
  if (ctx) return ctx
  return {
    onAccountReady: (accountId: number) =>
      navigate(`/admin/account/details/${accountId}`),
    onUserReady: (userId: number) => navigate(`/user/details/${userId}`),
    onCancel: () => navigate(-1),
  }
}
